import type { NewsArticle } from '@/lib/news/stockNews'
import { newsImageSrc } from '@/lib/news/newsImage'
import { Newspaper } from 'lucide-react'

/**
 * Presentational news list — rendered server-side on /stocks/[symbol] (crawlable, SEO) and inside the
 * client StockNewsPanel on the chart. Pure props, no hooks, so it works in both. Headlines link out to
 * the original source (fair-use aggregation). `import type` keeps the server-only news lib out of the
 * client bundle.
 */

function timeAgo(iso: string): string {
  if (!iso) return ''
  const then = new Date(iso).getTime()
  if (!Number.isFinite(then)) return ''
  const m = Math.floor((Date.now() - then) / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.floor(h / 24)
  if (d < 30) return `${d}d ago`
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function sentimentChip(s: NewsArticle['sentiment']) {
  if (s === 'positive') return { label: 'Bullish', cls: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' }
  if (s === 'negative') return { label: 'Bearish', cls: 'bg-rose-500/15 text-rose-600 dark:text-rose-300' }
  if (s === 'neutral') return { label: 'Neutral', cls: 'bg-muted text-muted-foreground' }
  return null
}

function NewsCard({ a, symbol }: { a: NewsArticle; symbol?: string }) {
  const chip = sentimentChip(a.sentiment)
  const sym = symbol || a.tickers[0]
  // Always show a thumbnail: the proxied publisher image, or the stock logo as a clean fallback.
  const thumb = newsImageSrc(a.imageUrl, sym) ?? (sym ? `/api/logo?symbol=${encodeURIComponent(sym)}` : null)
  return (
    <a href={a.url} target="_blank" rel="noopener noreferrer"
      className="group flex gap-3 rounded-xl border border-border bg-card p-3 transition-colors hover:border-muted-foreground/40 dark:hover:border-zinc-600 hover:bg-accent dark:hover:bg-zinc-800/50">
      {thumb && (
        <div className="hidden h-16 w-24 shrink-0 overflow-hidden rounded-lg bg-muted sm:block">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={thumb} alt="" className="h-full w-full object-cover" loading="lazy" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="mb-1 flex items-center gap-1.5 text-[11px] text-muted-foreground">
          {a.publisher.favicon && (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={a.publisher.favicon} alt="" width={14} height={14} className="h-3.5 w-3.5 rounded-sm object-contain" loading="lazy" />
          )}
          <span className="font-semibold text-foreground/70">{a.publisher.name}</span>
          <span>·</span>
          <span>{timeAgo(a.publishedUtc)}</span>
          {chip && <span className={`ml-auto shrink-0 rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide ${chip.cls}`}>{chip.label}</span>}
        </div>
        <h3 className="line-clamp-2 text-sm font-bold leading-snug text-foreground transition-colors group-hover:text-amber-600 dark:group-hover:text-amber-300">{a.title}</h3>
        {a.description && <p className="mt-1 line-clamp-2 text-[12px] leading-relaxed text-muted-foreground">{a.description}</p>}
        {/* Trading context: how the stock actually traded the news, not just its sentiment. */}
        {(a.priceAtNews != null || a.rvolAtNews != null) && (
          <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[10px]">
            {a.priceAtNews != null && (
              <span className="rounded bg-muted px-1.5 py-0.5 font-semibold tabular-nums text-foreground/80">
                {a.priceAtNews < 1 ? `$${a.priceAtNews.toFixed(3)}` : `$${a.priceAtNews.toFixed(2)}`} at release
              </span>
            )}
            {a.pctSinceNews != null && (
              <span className={`rounded px-1.5 py-0.5 font-bold tabular-nums ${a.pctSinceNews >= 0 ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' : 'bg-rose-500/15 text-rose-600 dark:text-rose-300'}`}>
                {a.pctSinceNews >= 0 ? '+' : ''}{a.pctSinceNews.toFixed(1)}% since
              </span>
            )}
            {a.rvolAtNews != null && a.rvolAtNews >= 1.2 && (
              <span className="rounded bg-amber-500/15 px-1.5 py-0.5 font-bold tabular-nums text-amber-700 dark:text-amber-300">{a.rvolAtNews.toFixed(1)}x volume</span>
            )}
          </div>
        )}
        {/* Other tickers this story is about — so a reader sees the full basket, and it's clear WHY the
            article is on this symbol's page. Rendered as chips (not links): the whole card is an <a>. */}
        {(() => {
          const others = (a.tickers || []).filter((t) => t && t.toUpperCase() !== (sym || '').toUpperCase()).slice(0, 6)
          return others.length ? (
            <div className="mt-1.5 flex flex-wrap items-center gap-1 text-[10px] text-muted-foreground">
              <span className="text-muted-foreground">Also mentioned:</span>
              {others.map((t) => (
                <span key={t} className="rounded bg-muted px-1 py-0.5 font-semibold text-muted-foreground">${t.toUpperCase()}</span>
              ))}
            </div>
          ) : null
        })()}
      </div>
    </a>
  )
}

export default function StockNews({ items, symbol, heading }: { items: NewsArticle[]; symbol?: string; heading?: string }) {
  if (!items.length) return null
  return (
    <section>
      <h2 className="mb-3 flex items-center gap-2 text-lg font-bold tracking-tight">
        <Newspaper className="h-4 w-4 text-amber-600 dark:text-amber-400" />
        {heading ?? (symbol ? `Latest ${symbol} News` : 'Latest Market News')}
      </h2>
      <div className="space-y-2">
        {items.map((a) => <NewsCard key={a.id} a={a} symbol={symbol} />)}
      </div>
      <p className="mt-2 text-[10px] text-muted-foreground">Data links to original article.</p>
    </section>
  )
}
